// File: ./src/AdContainer.js
//
// Google Consent Mode v2 (initialised in index.html) handles personalisation:
//   - With marketing consent    → personalised ads, advertising cookies set
//   - Without marketing consent → non-personalised ads (NPA), no cookies
//
// The AdSense script is loaded only after Cookiebot has resolved consent
// (either accepted or declined).  This prevents the script from expanding
// the ad container before consent is known, which would break layout tests
// in CI (where Cookiebot is absent and the consent events never fire).
//
// GCM v2 update signals are pushed in index.html and are independent of
// whether AdSense is loaded — Google reads them before serving any ad.
//
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useLang } from './LangContext';
import { getT } from './translations';
import useScript from './useScript';

/**
 * Returns true once Cookiebot has fired its first accept/decline event,
 * meaning consent is resolved (regardless of the decision).
 * Falls back to false in environments where Cookiebot is absent (e.g. CI).
 */
const useCookiebotResolved = () => {
  const [resolved, setResolved] = useState(() => {
    // Already resolved if Cookiebot has a consent object with a known value
    if (typeof window !== 'undefined' && window.Cookiebot && window.Cookiebot.hasResponse) {
      return true;
    }
    return false;
  });

  useEffect(() => {
    if (resolved) return;
    const onResolved = () => setResolved(true);
    window.addEventListener('CookiebotOnAccept', onResolved);
    window.addEventListener('CookiebotOnDecline', onResolved);
    return () => {
      window.removeEventListener('CookiebotOnAccept', onResolved);
      window.removeEventListener('CookiebotOnDecline', onResolved);
    };
  }, [resolved]);

  return resolved;
};

function AdContainer({ visible, onClose, disableClose }) {
  const adRef = useRef(null);
  const { lang } = useLang();
  const t = getT(lang);
  const consentResolved = useCookiebotResolved();

  // Load AdSense only after Cookiebot has resolved consent.
  // GCM v2 signals (pushed in index.html) tell Google whether to personalise.
  const scriptStatus = useScript(
    visible && consentResolved
      ? 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-3321586225695682'
      : null
  );

  const retryCount = useRef(0);
  const maxRetries = 10;

  const pushAd = useCallback(() => {
    if (adRef.current && adRef.current.offsetWidth > 0) {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch (error) {
        console.error('adsbygoogle push error:', error);
      }
    } else {
      if (retryCount.current < maxRetries) {
        retryCount.current++;
        console.warn(
          `Ad container width is 0, retrying adsbygoogle push. Attempt ${retryCount.current}`
        );
        setTimeout(pushAd, 200);
      } else {
        console.warn('Ad container width is still 0 after maximum retries, giving up.');
      }
    }
  }, []);

  useEffect(() => {
    if (visible && consentResolved && scriptStatus === 'ready' && window.adsbygoogle) {
      retryCount.current = 0;
      pushAd();
    }
  }, [visible, consentResolved, scriptStatus, pushAd]);

  // Reserve space in body layout when ad is visible
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const cls = 'has-sticky-ad';
    if (visible) {
      document.body.classList.add(cls);
    } else {
      document.body.classList.remove(cls);
    }
    return () => document.body.classList.remove(cls);
  }, [visible]);

  if (!visible) return null;

  return (
    <div id="ad-container" className="ad-vertical-container">
      <div
        id="ad-header"
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}
      >
        <div></div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span id="ad-label">{t.adLabel}</span>
          {!disableClose && (
            <button id="close-ad" onClick={onClose} aria-label={t.adCloseAria}>
              &times;
            </button>
          )}
        </div>
      </div>
      <div id="ad-content">
        {consentResolved ? (
          <ins
            className="adsbygoogle"
            ref={adRef}
            style={{ display: 'block', width: '100%', height: '100%', maxHeight: '100%', overflow: 'hidden' }}
            data-ad-client="ca-pub-3321586225695682"
            data-ad-slot="5545661090"
            data-ad-format="horizontal"
            data-full-width-responsive="true"
          ></ins>
        ) : null}
      </div>
    </div>
  );
}

export default AdContainer;
